import { isSabbath, spokenDate, weekdayName } from '../calendar'
import type { PublishedDay, Reckoning } from '../calendar'

/**
 * The page opens with the date, reckoned the way this community reckons it.
 *
 * Not "welcome back". A woman signing in here already knows who she is; what orients her is
 * where she stands in the month, how far off the Sabbath is, and which feast is coming. All of
 * that is true every day without anyone authoring it, and it is the one thing this site knows
 * that a generic account page could not.
 *
 * The day is spelled out because that is how it is said aloud. "5/18" is a database row.
 *
 * This is the front door's masthead. The signed-in deck states the date its own way, in
 * TodayColumn, and reuses only [CountedMonth] from here.
 */
export function Masthead({ reckoning }: { reckoning: Reckoning | null }) {
  if (!reckoning) {
    // The calendar is published by Torahly and cached locally. Missing means the fetch failed
    // with nothing cached, which is a quiet absence rather than an error worth shouting about.
    return null
  }

  const { today, month, daysToSabbath, upcomingFeasts } = reckoning

  return (
    <div className="masthead">
      <div className="masthead-date">
        <p className="date-spoken">{spokenDate(today)}</p>
        <p className="date-meta">
          {weekdayName(today)}
          {today.f ? ` · ${today.f}` : ''}
          {sabbathNote(daysToSabbath)}
        </p>
      </div>

      <CountedMonth month={month} today={today} />

      {upcomingFeasts.length > 0 && (
        <ul className="feast-list">
          {upcomingFeasts.map((feast) => (
            <li key={feast.d}>
              <span className="feast-name">{feast.f}</span>
              <span className="feast-when">{feastWhen(today.d, feast)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function sabbathNote(daysToSabbath: number | null): string {
  if (daysToSabbath === null || daysToSabbath === 0) return ''
  if (daysToSabbath === 1) return ' · Sabbath tomorrow'
  return ` · Sabbath in ${daysToSabbath} days`
}

function feastWhen(todayKey: string, feast: PublishedDay): string {
  const gap = Math.round(
    (new Date(`${feast.d}T00:00:00`).getTime() - new Date(`${todayKey}T00:00:00`).getTime()) /
      86400000,
  )
  if (gap === 0) return 'today'
  if (gap === 1) return 'tomorrow'
  return `in ${gap} days`
}

const WEEK_HEADS = ['1', '2', '3', '4', '5', '6', 'S']

/**
 * The month counted out as weeks.
 *
 * Seven columns, because the week is seven days. Laying it out this way means the Sabbath is a
 * column you can see rather than a fact you have to look up, and it is why the row of marks is
 * worth more than a plain strip of numbers.
 *
 * No boxes and no rules: a sheet of numerals, not a booking calendar. Days already past are
 * dimmed rather than dropped, so the month reads as something being counted through.
 *
 * The Sabbath is marked the way feast days are, because it is one.
 */
export function CountedMonth({ month, today }: { month: PublishedDay[]; today: PublishedDay }) {
  const first = month[0]
  if (!first) return null

  // Blank cells so the first of the month lands under its own weekday.
  const lead = first.w - 1

  return (
    <div className="counted-month" role="grid" aria-label={`Month ${today.m}`}>
      {WEEK_HEADS.map((head, index) => (
        <div key={head} className={index === 6 ? 'weekday-head sabbath-col' : 'weekday-head'}>
          {head}
        </div>
      ))}

      {Array.from({ length: lead }, (_, index) => (
        <span key={`lead-${index}`} aria-hidden="true" />
      ))}

      {month.map((day) => {
        const isToday = day.d === today.d
        const classes = ['day-mark']
        // Today is drawn as today and nothing else. Stacking is-feast on top of it would mean
        // fighting the two rules on specificity, and the filled mark already says "you are
        // here" more loudly than a colour could.
        if (isToday) classes.push('is-today')
        else if (day.f) classes.push('is-feast')
        else if (isSabbath(day)) classes.push('is-sabbath')
        if (day.d < today.d && !isToday) classes.push('is-past')

        return (
          <span
            key={day.d}
            className={classes.join(' ')}
            title={day.f ?? (isSabbath(day) ? 'Sabbath' : undefined)}
            aria-current={isToday ? 'date' : undefined}
          >
            {day.dy}
          </span>
        )
      })}
    </div>
  )
}
